 
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SFTypography,
  SFUseStyled,
  SFStack,
  SFAvatar,
  SFMoreMenu,
  SFButton,
} from "@sf-core";
import {
  getJobDetails,
  getSkillDetails,
} from "../../../../services/profileService";
import DetailsDialog from "../../../../components/dialog/detailsDialog";
import { useSelector, useDispatch } from "react-redux";
import { profileActions } from "../../../../redux/reducers/profileReducer";
import { getProfileUrl, getJobUrl } from "../../../../constants/urls";
import { PROFILE_STEPS } from "../../../../constants/profileSteps";
import { useNavigate } from "react-router-dom";
import { ColorFlag, ColorCase } from "../icons";
import { PROGRESS_TYPES } from "../../../../constants/progressTypes";
import skillThumbnail from "../../images/skill_thumbnail.png";

const { styled } = SFUseStyled();

const StyledSFStack = styled(SFStack)(({ theme }) => ({
  borderRadius: 22,
  background: "rgba(26, 26, 36, 0.30)",
  padding: 2,
  paddingLeft: 4,
  marginRight: 300,
}));

const SFAvatarStyled = styled(SFAvatar)(() => ({
  width: 34,
  height: 34,
  fontSize: 20,
}));

const CustomIcon = styled("div")(({ theme, hasFlagIcon = false }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 35,
  height: 35,
  borderRadius: "50%",
  background: theme.palette.grey500.main,
  border: "1px solid #B14891",
  ...(hasFlagIcon && {
    border: "1px solid #27ACAA",
  }),
}));

const CustomHeader = (props) => (
  <StyledSFStack
    direction="row"
    spacing={1}
    alignItems="center"
    justifyContent="space-between"
  >
    <SFStack direction="row" spacing={1} alignItems="center">
      {props.children}
      <SFStack>
        <SFTypography variant="body1">{props.title}</SFTypography>
        <SFTypography variant="body2bold">{props.subTitle}</SFTypography>
      </SFStack>
    </SFStack>
    <SFMoreMenu items={props.actions} />
  </StyledSFStack>
);

const ProgressDialogs = ({ dialog, setDialog }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [jobDetails, setJobDetails] = useState(null);
  const [skillDetails, setSkillDetails] = useState(null);
  const [badges, setBadges] = useState([]);
  const [skills, setSkills] = useState(null);
  const [dialogSettings, setDialogSettings] = useState(null);
  const userAvatar = useSelector((state) => state.session.avatar);
  const userJobRoleId = useSelector(
    (state) => state.userConfiguration.jobRoleId
  );

  useEffect(() => {
    if (dialog.skillId) {
      getSkillDetails(dialog.skillId).then((skillResponse) => {
        setSkillDetails(skillResponse.data);
        const skillMicroSkills = skillResponse.data.microSkills.map(
          (mircoSkill) => ({
            id: mircoSkill.microSkillCode,
            name: mircoSkill.microSkillName,
          })
        );
        setSkills(skillMicroSkills);
        setBadges([
          {
            label: t("profileScreen.detailsDialog.skillCategoryLabel"),
            description: skillResponse.data.category,
          },
          {
            label: t("profileScreen.detailsDialog.parentSkillLabel"),
            description: skillResponse.data.parentSkill,
          },
        ]);
      });
    } else {
      getJobDetails(dialog.jobId).then((jobResponse) => {
        setJobDetails(jobResponse.data);
        const jobSkills = jobResponse.data.successProfileSkills.map(
          (skill) => ({
            id: skill.skillId,
            name: skill.skillName,
            rank: skill.skillRank,
          })
        );
        setSkills(jobSkills.sort((a, b) => a.rank - b.rank));
        setBadges([
          {
            label: t("profileScreen.detailsDialog.businessCapabilityLabel"),
            description: jobResponse.data.businessCapabilityName,
          },
          {
            label: t("profileScreen.detailsDialog.disciplineLabel"),
            description: jobResponse.data.disciplineName,
          },
          {
            label: t("profileScreen.detailsDialog.jobFamilyLabel"),
            description: jobResponse.data.jobFamilyName,
            ignoreIcon: true,
          },
        ]);
      });
    }
  }, []);

  useEffect(() => {
    switch (dialog.type) {
    case PROGRESS_TYPES.JOB_ROLE:
      setDialogSettings({
        customHeader: (
          <CustomHeader
            subTitle={jobDetails?.jobName}
            title={t("dashboardScreen.dialogs.yourJobSelection")}
            actions={[
              {
                label: t("dashboardScreen.viewJob"),
                onClick: () => {
                  navigate(getJobUrl(userJobRoleId));
                },
              },
              {
                label: t("dashboardScreen.manageJob"),
                onClick: () => {
                  dispatch(
                    profileActions.setState({
                      activeStep: PROFILE_STEPS.JOB_ROLE,
                    })
                  );
                  navigate(getProfileUrl());
                },
              },
            ]}
          >
            <SFAvatarStyled src={userAvatar} />
          </CustomHeader>
        ),
        skillsTitle: t("dashboardScreen.dialogs.skillsRequired"),
        onDetailsButtonClick: () => {
          navigate(getJobUrl(userJobRoleId));
        },
      });
      break;
    case PROGRESS_TYPES.JOB_GOAL:
      setDialogSettings({
        customHeader: (
          <CustomHeader
            subTitle={jobDetails?.jobName}
            title={t("dashboardScreen.dialogs.jobsOfInterest")}
            actions={[
              {
                label: t("dashboardScreen.manageJobsOfInterest"),
                onClick: () => {
                  dispatch(
                    profileActions.setState({
                      activeStep: PROFILE_STEPS.JOB_INTEREST,
                    })
                  );
                  navigate(getProfileUrl());
                },
              },
            ]}
          >
            <CustomIcon hasFlagIcon>
              <ColorFlag height={23} width={23} />
            </CustomIcon>
          </CustomHeader>
        ),
        skillsTitle: t("dashboardScreen.dialogs.skillsRequired"),
        onDetailsButtonClick: () => {
          navigate(getJobUrl(jobDetails?.domainId));
        },
      });
      break;
    case PROGRESS_TYPES.JOB_SKILL:
      setDialogSettings({
        customHeader: (
          <CustomHeader
            subTitle={skillDetails?.name}
            title={t("dashboardScreen.dialogs.jobSkill")}
            actions={[
              {
                label: t("dashboardScreen.manageSkills"),
                onClick: () => {
                  dispatch(
                    profileActions.setState({
                      activeStep: PROFILE_STEPS.SKILLS,
                    })
                  );
                  navigate(getProfileUrl());
                },
              },
            ]}
          >
            <CustomIcon>
              <ColorCase height={21} width={21} />
            </CustomIcon>
          </CustomHeader>
        ),
        skillsTitle: t("dashboardScreen.dialogs.microSkills"),
      });
      break;
    case PROGRESS_TYPES.SKILL_GOAL:
      setDialogSettings({
        customHeader: (
          <CustomHeader
            subTitle={skillDetails?.name}
            title={t("dashboardScreen.dialogs.skillGoal")}
            actions={[
              {
                label: t("dashboardScreen.manageSkills"),
                onClick: () => {
                  dispatch(
                    profileActions.setState({
                      activeStep: PROFILE_STEPS.SKILL_GOAL,
                    })
                  );
                  navigate(getProfileUrl());
                },
              },
            ]}
          >
            <CustomIcon hasFlagIcon>
              <ColorFlag height={23} width={23} />
            </CustomIcon>
          </CustomHeader>
        ),
        skillsTitle: t("dashboardScreen.dialogs.microSkills"),
      });
      break;
    default:
      break;
    }
  }, [jobDetails, skillDetails]);

  return (
    <DetailsDialog
      open={jobDetails !== null || skillDetails !== null}
      titleThumbnail={dialog.skillId ? skillThumbnail : jobDetails?.thumbnail}
      setDialog={setDialog}
      customHeader={dialogSettings?.customHeader}
      showHierarchy={false}
      description={
        dialog.skillId ? skillDetails?.description : jobDetails?.jobDescription
      }
      badges={badges}
      proficiencyLevels={skillDetails}
      proficiencyLevel={dialog?.proficiencyLevel}
      skills={skills}
      skillsTitle={dialogSettings?.skillsTitle}
      actionsBoxShadow
      actionBtns={
        <SFStack direction="row" alignItems="center" spacing={2}>
          <SFButton
            variant="transparent"
            onClick={() => setDialog({ open: false })}
          >
            <SFTypography>{t("dashboardScreen.dialogs.closeBtn")}</SFTypography>
          </SFButton>
          {dialogSettings?.onDetailsButtonClick && (
            <SFButton
              variant="secondary"
              onClick={dialogSettings.onDetailsButtonClick}
            >
              <SFTypography>
                {t("dashboardScreen.dialogs.viewDetailsBtn")}
              </SFTypography>
            </SFButton>
          )}
        </SFStack>
      }
    />
  );
};

export default ProgressDialogs;
